import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  FaDownload, 
  FaWindows, 
  FaApple, 
  FaLinux, 
  FaHome, 
  FaSignOutAlt, 
  FaMicrochip,
  FaCheckCircle,
  FaShieldAlt,
  FaRocket,
  FaCog,
  FaPlug,
  FaBook,
  FaHeadset,
  FaQuestionCircle,
  FaArrowRight,
  FaServer
} from "react-icons/fa";
import Logo from "../components/Logo";
import "../styles/Downloads.css";

const Downloads = () => {
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState(null);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const downloadOptions = [
    {
      platform: "Windows",
      icon: <FaWindows />,
      description: "Full-featured Windows agent with native integration",
      gradient: "linear-gradient(135deg, #0078D4 0%, #00BCF2 100%)",
      accentColor: "#00BCF2",
      versions: [
        { 
          arch: "x86_64", 
          name: "Windows x64", 
          url: "https://downloads.triagz.com/TriagzAgentInstaller.zip",
          size: "93.72 MB",
          format: "MSI Installer"
        }
      ],
      requirements: [
        "Windows 10 or later",
        "Administrator privileges",
        "Internet connection",
        "100MB free disk space"
      ],
      features: ["Auto-updates", "System tray integration", "Native notifications"]
    },
    {
      platform: "macOS",
      icon: <FaApple />,
      description: "Optimized for Apple Silicon M-series processors",
      gradient: "linear-gradient(135deg, #A2AAAD 0%, #555555 100%)",
      accentColor: "#A2AAAD",
      versions: [
        { 
          arch: "arm64", 
          name: "Apple Silicon", 
          url: "https://downloads.triagz.com/TriagzAgent-Installer-v1.0.1.dmg.zip",
          size: "78.52 MB",
          format: "DMG Package"
        }
      ],
      requirements: [
        "macOS 11.0 (Big Sur) or later",
        "Apple Silicon (M1/M2/M3)",
        "Internet connection",
        "200MB free disk space"
      ],
      features: ["Native ARM64", "Menu bar app", "Keychain integration"]
    },
    {
      platform: "Linux",
      icon: <FaLinux />,
      description: "Lightweight agent for Linux distributions",
      gradient: "linear-gradient(135deg, #FCC624 0%, #DD4814 100%)",
      accentColor: "#FCC624",
      versions: [
        { 
          arch: "x86_64", 
          name: "Linux x64", 
          url: "https://downloads.triagz.com/triagz_agent_linux.zip",
          size: "8.5 MB",
          format: "ZIP Archive"
        }
      ],
      requirements: [
        "Linux kernel 5.10+",
        "Root privileges",
        "Internet connection",
        "100MB free disk space"
      ],
      features: ["Systemd service", "CLI interface", "Minimal footprint"]
    }
  ];

  const installSteps = [
    {
      icon: <FaDownload />,
      title: "Download",
      description: "Select the agent for your OS and architecture"
    },
    {
      icon: <FaCog />,
      title: "Install",
      description: "Run installer with admin/root privileges"
    },
    {
      icon: <FaShieldAlt />,
      title: "Configure",
      description: "Generate and add your agent key"
    },
    {
      icon: <FaPlug />,
      title: "Connect",
      description: "Verify connection in chat interface"
    }
  ];

  const handleDownload = (url, filename) => {
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      window.location.href = url;
    }
  };

  return (
    <div className="downloads-page">
      {/* Top Navigation Bar */}
      <nav className="top-navbar">
        <div className="navbar-content">
          <div className="navbar-left">
            <Logo size="small" showText={false} linkTo="/chat" />
            <div className="nav-links">
              <button 
                className="nav-link"
                onClick={() => navigate('/chat')}
              >
                <FaHome /> Chat
              </button>
              <button 
                className="nav-link active"
                onClick={() => navigate('/downloads')}
              >
                <FaDownload /> Downloads
              </button>
            </div>
          </div>
          <div className="navbar-right">
            <button 
              className="logout-btn"
              onClick={handleLogout}
            >
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="downloads-hero">
        <div className="hero-bg-pattern"></div>
        <div className="hero-glow"></div>
        <div className="downloads-hero-content">
          <h1>
            Deploy Triagz Agents
            <br />Across Your Infrastructure
          </h1>
          <p>
            Secure, lightweight agents for Windows, macOS, and Linux. 
            Get started in minutes with our easy-to-install packages.
          </p>
          <div className="hero-stats">
            <div className="stat-item">
              <FaServer />
              <div className="stat-info">
                <span className="stat-value">3</span>
                <span className="stat-label">Platforms</span>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <FaShieldAlt />
              <div className="stat-info">
                <span className="stat-value">256-bit</span>
                <span className="stat-label">Encryption</span>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <FaRocket />
              <div className="stat-info">
                <span className="stat-value">&lt; 2min</span>
                <span className="stat-label">Setup Time</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="downloads-container">
        {/* Downloads Grid */}
        <div className="downloads-grid">
          {downloadOptions.map((option, index) => (
            <div 
              key={index} 
              className={`download-card ${hoveredCard === index ? 'hovered' : ''}`}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="card-glow" style={{ background: option.gradient }}></div>
              
              <div className="card-header">
                <div className="platform-icon-wrapper" style={{ background: option.gradient }}>
                  {option.icon}
                </div>
                <div className="platform-meta">
                  <h3>{option.platform}</h3>
                  <span className="version-badge">v1.0.1</span>
                </div>
              </div>

              <p className="platform-description">{option.description}</p>

              <div className="features-list">
                {option.features.map((feature, fIndex) => (
                  <span key={fIndex} className="feature-tag">
                    <FaCheckCircle /> {feature}
                  </span>
                ))}
              </div>

              <div className="requirements-section">
                <h4>System Requirements</h4>
                <ul className="requirements-list">
                  {option.requirements.map((req, reqIndex) => (
                    <li key={reqIndex}>
                      <span className="req-check">✓</span>
                      {req}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="download-actions">
                {option.versions.map((version, versionIndex) => (
                  <div key={versionIndex} className="download-option">
                    <div className="download-info">
                      <div className="arch-badge">
                        <FaMicrochip />
                        <span>{version.arch}</span>
                      </div>
                      <div className="file-details">
                        <span className="file-format">{version.format}</span>
                        <span className="file-size">{version.size}</span>
                      </div>
                    </div>
                    <button 
                      className="download-btn"
                      onClick={() => handleDownload(version.url, `${option.platform}-${version.arch}-agent`)}
                    >
                      <FaDownload />
                      <span>Download</span>
                      <FaArrowRight className="btn-arrow" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Installation Timeline */}
        <section className="installation-section">
          <div className="section-header">
            <h2>Quick Installation</h2>
            <p>Get up and running in four simple steps</p>
          </div>
          
          <div className="installation-timeline">
            {installSteps.map((step, index) => (
              <div key={index} className="timeline-step">
                <div className="step-connector">
                  {index < installSteps.length - 1 && <div className="connector-line"></div>}
                </div>
                <div className="step-icon">
                  {step.icon}
                  <span className="step-number">{index + 1}</span>
                </div>
                <div className="step-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Support Section */}
        <section className="support-section">
          <div className="support-card">
            <div className="support-icon">
              <FaHeadset />
            </div>
            <div className="support-content">
              <h3>Need Help Getting Started?</h3>
              <p>
                Our documentation covers everything from installation to advanced configuration. 
                Can't find what you need? Our support team is here to help.
              </p>
              <div className="support-actions">
                <Link to="/blog/7" className="support-btn primary">
                  <FaBook /> Documentation
                </Link>
                <Link to="/report-bug" className="support-btn secondary">
                  <FaHeadset /> Contact Support
                </Link>
                <Link to="/blog" className="support-btn secondary">
                  <FaQuestionCircle /> FAQ
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Downloads;
